/* ============================================================================
   EVE BUNDLE BUILDER v3 — tiered "buy 2 get 1 free / buy 3 get 2 free" engine
   - Card image carousel: scroll-snap + dots + hover arrows (no dependencies)
   - "Add" slides up a variant panel with the first available variant
     PRE-SELECTED, so "Add To Box" works in one tap
   - Adds to the native Shopify cart (/cart/add.js)
   - Sticky tray with TWO milestones (m1 = first free piece, m2 = full deal),
     greedy beyond m2: free(n) = m2Free*floor(n/m2) + (n%m2 >= m1 ? m1Free : 0).
     Before the first free piece the deal-framing button nudges toward m1;
     from the first free piece on, the main button is always CHECKOUT at the
     live cart total (never block someone who already earned a free piece).
     The second line ladders to the next milestone / shows the live savings.
   - Quick View modal: gallery + options + Add To Box
   - Tray stays in sync with outside cart changes (header drawer remove,
     cart page) via the theme's cart:change / cart:refresh events
   Pricing is enforced by the tiered discount function (custom app
   aylagleam-bundle-deals, lowest-priced items 100% off) — this file is UI only.
   ========================================================================== */
(function () {
  'use strict';

  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    // html lang is de kale taal ("es"); Intl rendert "es"+MXN als "1797,00 MXN".
    // Met de marktregio erbij ("es-MX") wordt het native "$1,797.00".
    var lang = document.documentElement.lang || 'en';
    var country = (window.Shopify && window.Shopify.country) || 'MX';
    var loc = lang.indexOf('-') === -1 ? lang + '-' + country : lang;
    var opts = { style: 'currency', currency: cur };
    // Alle callers geven integer-centen door — hele bedragen zonder ",00" tonen.
    if (cents % 100 === 0) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
    try {
      return new Intl.NumberFormat(loc, opts).format(cents / 100);
    } catch (e) {
      try { return new Intl.NumberFormat(lang, opts).format(cents / 100); } catch (e2) {}
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // Placeholder-substitutie zonder String.replace zodat '$' in bedragen
  // ("$85.00") nooit als replacement-pattern wordt geïnterpreteerd.
  function sub(tpl, key, val) {
    return String(tpl).split(key).join(String(val));
  }

  function req(url, opts) {
    return fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }, opts))
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok) {
            var e = new Error(body.description || body.message || 'Request failed');
            e.body = body;
            throw e;
          }
          return body;
        });
      });
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Shopify-CDN-beelden on-the-fly herschalen: de kaart-thumbs (120/200px) zijn te
  // klein voor de sheet; width/height in de src-queryparams ophogen levert een
  // scherpe variant zonder extra Liquid-output (crop blijft staan).
  function hiRes(html, px) {
    return String(html || '').replace(/src="([^"]+)"/g, function (m, src) {
      // In geserialiseerde HTML staat & als &amp; — beide vormen matchen.
      return 'src="' + src.replace(/([?&]|&amp;)width=\d+/, '$1width=' + px).replace(/([?&]|&amp;)height=\d+/, '$1height=' + px) + '"';
    });
  }

  /* ---------------- image slider (scroll-snap) ---------------- */
  // opts.isDragged (optioneel): swipe-guard van de EveBundle-instantie. Alleen de
  // quick-view-galerij geeft hem mee; dan wordt een tap op de foto = volgende foto.
  function initSlider(slider, opts) {
    if (!slider || slider.__init) return;
    slider.__init = true;
    var track = slider.querySelector('[data-slides]');
    var dots = slider.querySelectorAll('.eve-bundle__dot');
    var prev = slider.querySelector('[data-slide-prev]');
    var next = slider.querySelector('[data-slide-next]');
    if (!track) return;
    var isDragged = opts && typeof opts.isDragged === 'function' ? opts.isDragged : null;

    function slideW() { return track.clientWidth || 1; }
    function index() { return Math.round(track.scrollLeft / slideW()); }
    function go(i) { track.scrollTo({ left: i * slideW(), behavior: 'smooth' }); }

    // Quick view: tap op de foto = volgende foto (laatste → terug naar 1).
    // Clarity 01-09 + 04-09: 71% van de lege dode kliks op de bundel-LP viel hier.
    // Kaartgalerij blijft ongemoeid (daar opent een tap de quick view via de root-handler).
    if (isDragged && slider.closest('.eve-qv__gallery') && track.children.length > 1) {
      track.addEventListener('click', function () {
        if (isDragged()) return; // einde van een swipe telt niet als tap
        var last = track.children.length - 1;
        go(index() >= last ? 0 : index() + 1);
      });
    }

    if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); go(Math.max(0, index() - 1)); });
    if (next) next.addEventListener('click', function (e) { e.stopPropagation(); go(index() + 1); });

    // clickable dots (LC parity)
    dots.forEach(function (d, di) {
      d.addEventListener('click', function (e) { e.stopPropagation(); go(di); });
    });

    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var i = index();
        dots.forEach(function (d, di) { d.classList.toggle('is-active', di === i); });
      });
    }, { passive: true });
  }

  /* ---------------- main component ---------------- */
  function EveBundle(root) {
    this.root = root;
    // m2 (= data-bundle-size) is de "volle deal" (5 → 2 gratis); m1 is de
    // eerste milestone (3 → 1 gratis). Greedy doortellen voorbij m2.
    this.size = parseInt(root.getAttribute('data-bundle-size'), 10) || 5;
    this.m1 = parseInt(root.getAttribute('data-m1'), 10) || 3;
    this.m1Free = parseInt(root.getAttribute('data-m1-free'), 10) || 1;
    this.m2Free = parseInt(root.getAttribute('data-m2-free'), 10) || 2;
    this.countAllCart = root.getAttribute('data-count-all-cart') === 'true';
    this.addedText = root.getAttribute('data-added-text') || 'Added To Box ✓';
    this.addToBoxText = root.getAttribute('data-addtobox-text') || 'Add To Box';
    this.addAddedText = root.getAttribute('data-add-added-text') || 'Added ✓';
    this.countTpl = root.getAttribute('data-count-text') || "{count} in your box";
    this.countFreeTpl = root.getAttribute('data-count-free-text') || '';
    this.errorText = root.getAttribute('data-error-text') || 'Try again';
    this._traySeq = 0;
    this.requireFull = root.getAttribute('data-require-full') === 'true';
    this.showPrices = root.getAttribute('data-show-prices') === 'true';
    this.qvRating = root.getAttribute('data-qv-rating') === 'true';
    this.qvReviewsLabel = root.getAttribute('data-qv-reviews-label') || 'verified reviews';
    this.nudge1Tpl = root.getAttribute('data-nudge1-text') || '';
    this.nudge2Tpl = root.getAttribute('data-nudge2-text') || '';
    this.addMoreTpl = root.getAttribute('data-add-more-text') || '';
    this.checkoutTotalTpl = root.getAttribute('data-checkout-complete-text') || '';
    this.savingsTpl = root.getAttribute('data-savings-text') || '';
    this.showSavings = root.getAttribute('data-show-savings') === 'true';
    this.inboxTpl = root.getAttribute('data-inbox-text') || '';
    this.dealFraming = root.getAttribute('data-deal-framing') === 'true';
    this.unlockTpl = root.getAttribute('data-unlock-button-text') || '';
    this.checkoutNowTpl = root.getAttribute('data-checkout-now-text') || '';
    this.strikeTotal = root.getAttribute('data-strike-total') === 'true';
    this.openDrawerMilestones = root.getAttribute('data-open-drawer-milestones') === 'true';
    this._lastState = null;

    this.tray = root.querySelector('[data-tray]');
    this.trayBar = root.querySelector('[data-tray-bar]');
    this.trayCount = root.querySelector('[data-tray-count]');
    this.trayValue = root.querySelector('[data-tray-value]');
    this.trayCheckout = root.querySelector('[data-tray-checkout]');
    this.trayCheckoutLabel = root.querySelector('[data-checkout-label]');
    this.trayCheckoutSub = root.querySelector('[data-checkout-sub]');
    // Fotostrip bestaat alleen als de setting aanstaat (Liquid rendert 'm dan)
    this.strip = root.querySelector('[data-tray-strip]');
    this.stripThumbs = root.querySelector('[data-strip-thumbs]');
    this.checkoutFallbackText = ((this.trayCheckoutLabel || this.trayCheckout || {}).textContent || 'Checkout').trim();

    this.poolIds = {};
    var self = this;
    root.querySelectorAll('[data-card]').forEach(function (card) {
      self.poolIds[card.getAttribute('data-product-id')] = card;
    });

    // Reveal-state: 0 = uit (alle kaarten zichtbaar, oude gedrag)
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-card]'));
    this.revealInitial = parseInt(root.getAttribute('data-reveal-initial'), 10) || 0;
    this.revealStep = parseInt(root.getAttribute('data-reveal-step'), 10) || 24;
    this.revealAuto = root.getAttribute('data-reveal-auto') === 'true';
    this.revealMoreTpl = root.getAttribute('data-reveal-more-text') || '';
    this.revealLimit = this.revealInitial;
    this.activeFilters = [];
    this.moreBtn = root.querySelector('[data-reveal-more]');

    root.querySelectorAll('[data-slider]').forEach(initSlider);
    this.bind();
    this.renderGrid();
    this.initRevealObserver();
    this.refreshTray();

    // Tray in sync houden met cart-wijzigingen van buiten deze sectie
    // (header cart drawer, cart page). Prestige dispatcht cart:change
    // (bubbles, met detail.cart) bij elke mutatie; cart:refresh is de
    // "haal opnieuw van de server"-broadcast zonder detail.
    document.addEventListener('cart:change', function (e) {
      var cart = e && e.detail && e.detail.cart;
      if (cart && cart.items && typeof cart.total_price === 'number' && typeof cart.original_total_price === 'number') {
        // Deze payload is verser dan elke lopende /cart.js-fetch: in-flight
        // refreshes ongeldig maken zodat een stale response dit niet overschrijft.
        self._traySeq++;
        var st = self.cartState(cart);
        self.renderInbox(st);
        self.renderTray(st);
      } else {
        self.refreshTray();
      }
    });
    document.addEventListener('cart:refresh', function () { self.refreshTray(); });
    // bfcache terug-navigatie (bv. vanaf de cart-pagina) toont anders een stale count;
    // endCheckout laat de tray-CTA + loading-bar herleven na back-button vanuit checkout
    window.addEventListener('pageshow', function (e) { self.endCheckout(); if (e.persisted) self.refreshTray(); });
  }

  EveBundle.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var t = e.target;

      var toggle = t.closest('[data-add-toggle]');
      if (toggle) { self.openPanel(toggle.closest('[data-card]')); return; }

      if (t.closest('[data-variants-close]')) {
        var p = t.closest('[data-variants-panel]');
        if (p) p.classList.remove('is-open');
        return;
      }

      var optVal = t.closest('[data-option-value]');
      if (optVal) { self.selectOption(optVal); return; }

      var addBtn = t.closest('[data-addtobox]');
      if (addBtn) { self.addToBox(addBtn); return; }

      var setBtn = t.closest('[data-set-add]');
      if (setBtn) {
        // Bevestigingsknop in de set-sheet: sluiten kan pas als de add slaagt
        // (cart:refresh vuurt alleen in het succes-pad van addSetBox).
        if (setBtn.closest('[data-qv]')) {
          document.addEventListener('cart:refresh', function () { self.closeQuickView(); }, { once: true });
        }
        self.addSetBox(setBtn);
        return;
      }

      // Hele set-kaart = tap-target (Clarity: dead clicks op foto/titel); de
      // knop-tak hierboven vangt directe knop-kliks al af. Kaart-tap opent een
      // bevestigings-sheet i.p.v. direct te adden: oriëntatie-taps gaven 3 items
      // + prijsschok en vervuilden het ATC-signaal (diagnose funnel-drop 18-08).
      var setCard = t.closest('[data-set-card]');
      if (setCard) {
        var cardBtn = setCard.querySelector('[data-set-add]');
        if (cardBtn) { self.openSetConfirm(self.setInfoFromCard(setCard, cardBtn)); return; }
      }

      // Stuk in de set-sheet (thumb of hero) → product-Quick View met bladeren over de set
      var sheetQv = t.closest('[data-sheet-qv]');
      if (sheetQv && sheetQv.getAttribute('data-handle')) {
        var sheetEl = sheetQv.closest('[data-qv-body]') || self.root;
        var handles = [];
        var nodes = sheetEl.querySelectorAll('[data-sheet-qv][data-handle]');
        for (var hi = 0; hi < nodes.length; hi++) {
          var h = nodes[hi].getAttribute('data-handle');
          if (h && handles.indexOf(h) === -1) handles.push(h);
        }
        var cur = sheetQv.getAttribute('data-handle');
        self.openQuickView(cur, { handles: handles, index: Math.max(handles.indexOf(cur), 0) });
        return;
      }

      var qv = t.closest('[data-quickview]');
      if (qv) { e.preventDefault(); self.openQuickView(qv.getAttribute('data-handle')); return; }

      if (t.closest('[data-qv-prev]')) { self.stepQuickView(-1); return; }
      if (t.closest('[data-qv-next]')) { self.stepQuickView(1); return; }

      var filter = t.closest('[data-filter]');
      if (filter) { self.applyFilter(filter); return; }

      if (t.closest('[data-reveal-more]')) { self.revealMore(); return; }

      if (t.closest('[data-qv-close]')) { self.closeQuickView(); return; }

      var inboxBtn = t.closest('[data-inbox-remove]');
      if (inboxBtn) { self.removeProduct(inboxBtn.closest('[data-card]'), inboxBtn); return; }

      if (t.closest('[data-tray-view-cart]')) { self.openCartDrawer(0); return; }

      var checkout = t.closest('[data-tray-checkout]');
      if (checkout) {
        // Checkout-navigatie loopt al: herklik zou hem herstarten (86cb3gu56)
        if (checkout.classList.contains('is-loading')) { e.preventDefault(); return; }
        if (checkout.classList.contains('is-framed')) {
          // Deal-framing (<N items): hoofdknop stuurt terug naar het grid,
          // alleen de sub-regel ("or checkout now at …") gaat naar checkout.
          e.preventDefault();
          if (t.closest('[data-checkout-sub]')) { self.startCheckout(checkout); window.location.href = '/checkout'; }
          else { self.nudgeToGrid(); }
          return;
        }
        if (checkout.classList.contains('is-incomplete')) {
          // Vergrendelde checkout (require-full aan): stuur de klik terug naar het grid
          e.preventDefault();
          self.nudgeToGrid();
        } else if (checkout.classList.contains('is-disabled')) {
          e.preventDefault();
        } else {
          // Complete/open state: native navigatie naar /checkout laten
          // doorlopen — nu mét zichtbare bezig-state (traag MX-netwerk)
          self.startCheckout(checkout);
        }
        return;
      }

      // klik op de card-foto opent ook de quick view (dots/arrows/knoppen stoppen
      // propagation of zijn hierboven al afgehandeld); genegeerd na een swipe
      var media = t.closest('.eve-bundle__media');
      if (media && !self._dragged) {
        var mcard = media.closest('[data-card]');
        var qvBtn = mcard && mcard.querySelector('[data-quickview]');
        if (qvBtn) { self.openQuickView(qvBtn.getAttribute('data-handle')); return; }
      }
    });

    // swipe-guard: click na een sleep/scroll van >10px niet als klik behandelen
    this._dragged = false;
    this.root.addEventListener('pointerdown', function (e) {
      self._px = e.clientX; self._py = e.clientY; self._dragged = false;
    }, { passive: true });
    this.root.addEventListener('pointerup', function (e) {
      self._dragged = Math.abs(e.clientX - self._px) > 10 || Math.abs(e.clientY - self._py) > 10;
    }, { passive: true });
    this.root.addEventListener('pointercancel', function () { self._dragged = true; }, { passive: true });

    // variant-panel sluit bij klik buiten het panel (niet op de Add-knop die 'm opent) en bij Escape
    document.addEventListener('click', function (e) {
      var open = self.root.querySelector('[data-variants-panel].is-open');
      if (!open) return;
      if (e.target.closest('[data-variants-panel]') || e.target.closest('[data-add-toggle]')) return;
      open.classList.remove('is-open');
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = self.root.querySelector('[data-variants-panel].is-open');
      if (open) open.classList.remove('is-open');
      self.closeQuickView();
    });
    // toetsenbord op de tray-info (role="button"): Enter/spatie opent de cart drawer
    this.root.addEventListener('keydown', function (e) {
      if ((e.key === 'Enter' || e.key === ' ') && e.target.closest && e.target.closest('[data-tray-view-cart]')) {
        e.preventDefault();
        self.openCartDrawer(0);
      }
    });
  };

  /* ---------------- variant panel ---------------- */
  EveBundle.prototype.openPanel = function (card) {
    if (!card) return;
    this.root.querySelectorAll('[data-variants-panel].is-open').forEach(function (p) { p.classList.remove('is-open'); });
    var panel = card.querySelector('[data-variants-panel]');
    if (!panel) return;
    if (!panel.__defaulted) { this.selectDefaults(panel); panel.__defaulted = true; }
    panel.classList.add('is-open');
  };

  // Pre-select the options of the first AVAILABLE variant (LC behavior: defaults ready to add)
  EveBundle.prototype.selectDefaults = function (panel) {
    var data = this.variantData(panel);
    var first = null;
    for (var i = 0; i < data.length; i++) { if (data[i].available) { first = data[i]; break; } }
    if (!first) return;
    panel.querySelectorAll('[data-option-index]').forEach(function (group, gi) {
      var want = first.options[gi];
      group.querySelectorAll('[data-option-value]').forEach(function (b) {
        b.classList.toggle('is-selected', b.getAttribute('data-option-value') === want);
      });
    });
    this.resolveVariant(panel);
  };

  EveBundle.prototype.variantData = function (panel) {
    try { return JSON.parse(panel.querySelector('[data-variant-json]').textContent); }
    catch (e) { return []; }
  };

  EveBundle.prototype.selectOption = function (btn) {
    var group = btn.closest('[data-option-index]');
    if (!group) return;
    group.querySelectorAll('[data-option-value]').forEach(function (b) { b.classList.remove('is-selected'); });
    btn.classList.add('is-selected');
    this.resolveVariant(btn.closest('[data-variants-panel]'));
  };

  EveBundle.prototype.resolveVariant = function (panel) {
    if (!panel) return;
    var selected = [];
    var complete = true;
    panel.querySelectorAll('[data-option-index]').forEach(function (g) {
      var sel = g.querySelector('.is-selected');
      selected.push(sel ? sel.getAttribute('data-option-value') : null);
      if (!sel) complete = false;
    });

    var addBtn = panel.querySelector('[data-addtobox]');
    if (!addBtn) return;
    if (!complete) { addBtn.disabled = true; addBtn.removeAttribute('data-variant-id'); return; }

    var match = this.variantData(panel).find(function (v) {
      return v.options.length === selected.length && v.options.every(function (o, i) { return o === selected[i]; });
    });

    // Variant-image swap — alleen de JS-gebouwde QV-JSON bevat featured_image;
    // de Liquid card-overlay JSON niet, dus daar is dit een no-op.
    if (match && match.featured_image && match.featured_image.src) {
      var swapTrack = panel.parentElement ? panel.parentElement.querySelector('[data-slides]') : null;
      var swapImg = swapTrack ? swapTrack.querySelector('img') : null;
      if (swapImg) {
        var newSrc = match.featured_image.src +
          (match.featured_image.src.indexOf('?') > -1 ? '&width=700' : '?width=700');
        if (swapImg.getAttribute('src') !== newSrc) {
          swapImg.src = newSrc;
          swapTrack.scrollLeft = 0; // dots syncen via de bestaande scroll-listener
        }
      }
    }

    if (match && match.available) {
      addBtn.disabled = false;
      addBtn.setAttribute('data-variant-id', match.id);
    } else {
      addBtn.disabled = true;
      addBtn.removeAttribute('data-variant-id');
    }
  };

  /* ---------------- add to cart ---------------- */
  EveBundle.prototype.addToBox = function (btn) {
    var id = btn.getAttribute('data-variant-id');
    if (!id || btn.classList.contains('is-loading')) return;
    var self = this;
    btn.classList.add('is-loading');
    // Pre-add stand vastleggen voor de milestone-check (vóór de POST — een
    // cart:change tijdens de flight zou _lastState al op ná-add zetten).
    var prevCount = this._lastState ? this._lastState.count : null;
    req('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] }) })
      .then(function () {
        btn.classList.remove('is-loading');
        // let the theme update its cart count + drawer content from the server
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        var refreshed = self.refreshTray();
        var panel = btn.closest('[data-variants-panel]');
        if (panel) {
          // "ADDED TO BOX" feedback, then slide the panel back down (LC behavior)
          btn.classList.add('is-added');
          setTimeout(function () {
            btn.classList.remove('is-added');
            panel.classList.remove('is-open');
          }, 1100);
        } else {
          // direct add from a single-variant card button
          var label = btn.querySelector('span');
          if (label) {
            if (!btn.__origText) btn.__origText = label.textContent;
            label.textContent = self.addAddedText;
            setTimeout(function () { label.textContent = btn.__origText; }, 1400);
          }
        }
        // Milestone gecrosst (3, 5, 8, 10, …)? → cart drawer openen richting checkout
        if (self.openDrawerMilestones) {
          refreshed.then(function () {
            var st = self._lastState;
            if (!st) return;
            var oldC = prevCount == null ? Math.max(st.count - 1, 0) : prevCount;
            if (self.freeFor(st.count) > self.freeFor(oldC)) self.openCartDrawer(600);
          });
        }
      })
      .catch(function () {
        // failed add (sold out, cart error): show a brief retry hint, never a false success
        btn.classList.remove('is-loading');
        var label = btn.querySelector('.eve-bundle__boxbtn-label') || btn.querySelector('span');
        if (label) {
          if (!btn.__origText) btn.__origText = label.textContent;
          label.textContent = self.errorText;
          setTimeout(function () { label.textContent = btn.__origText; }, 1600);
        }
      });
  };

  // Set-kaart in het grid: alle 3 stuks in één batch-add (atomair — 422 = niets
  // toegevoegd), zelfde flow als de starter boxes. Een trio-add crost altijd
  // een milestone, dus de drawer-check draait op de pre-add stand.
  EveBundle.prototype.addSetBox = function (btn) {
    var ids = (btn.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
    if (ids.length === 0 || btn.classList.contains('is-loading')) return;
    var self = this;
    btn.classList.add('is-loading');
    var prevCount = this._lastState ? this._lastState.count : null;
    var label = btn.querySelector('[data-set-label]');
    var orig = label ? label.textContent : '';
    req('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: ids.map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; }) })
    })
      .then(function () {
        btn.classList.remove('is-loading');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        var refreshed = self.refreshTray();
        if (label) {
          label.textContent = btn.getAttribute('data-added-text') || orig;
          setTimeout(function () { label.textContent = orig; }, 2000);
        }
        if (self.openDrawerMilestones) {
          refreshed.then(function () {
            var st = self._lastState;
            if (!st) return;
            var oldC = prevCount == null ? Math.max(st.count - ids.length, 0) : prevCount;
            if (self.freeFor(st.count) > self.freeFor(oldC)) self.openCartDrawer(600);
          });
        }
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        if (label) {
          label.textContent = self.errorText;
          setTimeout(function () { label.textContent = orig; }, 1600);
        }
      });
  };

  /* ---------------- set-bevestigingssheet ---------------- */
  // Kaart-tap toont eerst een preview; pas de [data-set-add]-knop ín de sheet
  // voert de echte add uit (via de bestaande gedelegeerde tak + addSetBox).
  EveBundle.prototype.setInfoFromCard = function (card, btn) {
    var img = card.querySelector('.eve-bundle__set-img');
    var thumbs = card.querySelector('.eve-bundle__set-thumbs');
    var title = card.querySelector('.eve-bundle__title');
    var type = card.querySelector('.eve-bundle__type');
    var price = card.querySelector('.eve-bundle__set-price');
    var flag = card.querySelector('.eve-bundle__set-flag');
    var label = btn.querySelector('[data-set-label]');
    var pieces = card.querySelector('template[data-set-pieces]');
    return {
      piecesHtml: pieces ? pieces.innerHTML : '',
      mediaHtml: img ? img.outerHTML : (thumbs ? thumbs.outerHTML : ''),
      title: title ? title.textContent : '',
      sub: type ? type.textContent : '',
      priceHtml: price ? price.innerHTML : '',
      badge: flag ? flag.textContent : '',
      variantIds: btn.getAttribute('data-variant-ids') || '',
      addedText: btn.getAttribute('data-added-text') || '',
      ctaLabel: label ? label.textContent : ''
    };
  };

  EveBundle.prototype.openSetConfirm = function (info) {
    var qv = this.root.querySelector('[data-qv]');
    if (!qv || !info || !info.variantIds) return;
    var body = qv.querySelector('[data-qv-body]');
    var nav = qv.querySelector('[data-qv-nav]');
    if (nav) nav.hidden = true;
    this._qvNav = null;
    // Teksten komen uit section-settings (data-attrs op [data-qv]); Spaanse
    // fallback zodat een oudere section-versie de sheet niet leeg toont.
    var heading = qv.getAttribute('data-confirm-heading') || '¿Agregar este set a tu caja?';
    var cancel = qv.getAttribute('data-confirm-cancel') || 'Seguir viendo';
    var hint = qv.getAttribute('data-confirm-hint') || 'Toca una pieza para verla de cerca';
    // v2: stukken-rij (tap = product-Quick View). Recordings toonden dode taps op
    // foto/titel in de sheet — bezoekers willen de stukken zien vóór ze bevestigen.
    var pieces = hiRes(info.piecesHtml || '', 240);
    var mediaHtml = hiRes(info.mediaHtml || '', 720);
    var firstHandle = (pieces.match(/data-handle="([^"]+)"/) || [])[1] || '';
    var heroAttrs = firstHandle ? ' data-sheet-qv data-handle="' + esc(firstHandle) + '"' : '';
    body.innerHTML =
      '<div class="eve-setconfirm">' +
        '<p class="eve-setconfirm__heading">' + esc(heading) + '</p>' +
        '<div class="eve-setconfirm__media"' + heroAttrs + '>' + mediaHtml +
          (info.badge ? '<span class="eve-bundle__set-flag">' + esc(info.badge) + '</span>' : '') +
        '</div>' +
        (pieces ? '<div class="eve-setconfirm__pieces">' + pieces + '</div>' +
                  '<p class="eve-setconfirm__hint">' + esc(hint) + '</p>' : '') +
        '<h3 class="eve-setconfirm__title">' + esc(info.title) + '</h3>' +
        (info.sub ? '<p class="eve-setconfirm__sub">' + esc(info.sub) + '</p>' : '') +
        (info.priceHtml ? '<p class="eve-bundle__set-price eve-setconfirm__price">' + info.priceHtml + '</p>' : '') +
        '<button type="button" class="eve-bundle__set-cta eve-setconfirm__cta" data-set-add' +
          ' data-variant-ids="' + esc(info.variantIds) + '"' +
          (info.addedText ? ' data-added-text="' + esc(info.addedText) + '"' : '') + '>' +
          '<span data-set-label>' + esc(info.ctaLabel) + '</span>' +
        '</button>' +
        '<button type="button" class="eve-setconfirm__cancel" data-qv-close>' + esc(cancel) + '</button>' +
      '</div>';
    qv.hidden = false;
    document.documentElement.classList.add('eve-qv-open');
  };

  /* ---------------- sticky tray ---------------- */
  // Aantal gratis items bij n stuks (identiek aan de discount function).
  EveBundle.prototype.freeFor = function (n) {
    var free = this.m2Free * Math.floor(n / this.size);
    if (n % this.size >= this.m1) free += this.m1Free;
    return free;
  };

  // Bij hoeveel stuks de vólgende gratis unlockt (greedy ladder).
  EveBundle.prototype.nextTarget = function (n) {
    var r = n % this.size;
    return r < this.m1 ? n + (this.m1 - r) : n + (this.size - r);
  };

  // Whole-store deal (count_all_cart aan): élk cart-item telt mee en de
  // fotostrip toont de hele cart. Anders alleen pool-producten (het grid).
  // Totalen zijn altijd hele-cart (= wat de checkout écht rekent).
  EveBundle.prototype.cartState = function (cart) {
    var self = this;
    var count = 0;
    var byProduct = {};
    var items = [];
    (cart.items || []).forEach(function (item) {
      var inPool = !!self.poolIds[item.product_id];
      if (inPool) {
        byProduct[item.product_id] = (byProduct[item.product_id] || 0) + item.quantity;
      }
      if (self.countAllCart || inPool) {
        count += item.quantity;
        var img = item.image || '';
        if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=120';
        items.push({
          key: item.key,
          productId: item.product_id,
          title: item.product_title || item.title || '',
          variant: (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '',
          qty: item.quantity,
          image: img,
          linePrice: typeof item.final_line_price === 'number' ? item.final_line_price : null
        });
      }
    });
    return {
      count: count,
      byProduct: byProduct,
      items: items,
      total: typeof cart.total_price === 'number' ? cart.total_price : null,
      originalTotal: typeof cart.original_total_price === 'number' ? cart.original_total_price : null
    };
  };

  // "In your box"-badge + kaart-highlight voor elk poolproduct dat in de cart zit.
  // Bewust los van renderTray: die returnt vroeg als de tray uitgeschakeld is.
  EveBundle.prototype.renderInbox = function (state) {
    var self = this;
    Object.keys(this.poolIds).forEach(function (pid) {
      var card = self.poolIds[pid];
      var qty = (state.byProduct && state.byProduct[pid]) || 0;
      card.classList.toggle('is-in-box', qty > 0);
      var badge = card.querySelector('[data-inbox]');
      if (!badge) return;
      if (qty > 0) {
        var label = self.inboxTpl || 'In your box ✓';
        label = label.indexOf('{qty}') > -1
          ? sub(label, '{qty}', qty)
          : (qty > 1 ? label + ' ×' + qty : label);
        var labelEl = badge.querySelector('[data-inbox-label]');
        (labelEl || badge).textContent = label;
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    });
    // Een product dat via Quick View of een preset-box in de box belandde kan
    // voorbij de reveal-limiet liggen; renderGrid haalt 'm dan naar voren.
    this.renderGrid();
  };

  EveBundle.prototype.refreshTray = function () {
    var self = this;
    var seq = ++this._traySeq;
    return req('/cart.js').then(function (cart) {
      if (seq !== self._traySeq) return; // a newer refresh is in flight — drop stale response
      var st = self.cartState(cart);
      self.renderInbox(st);
      self.renderTray(st);
    }).catch(function () {});
  };

  EveBundle.prototype.renderTray = function (state) {
    if (!this.tray) return;
    this._lastState = state;
    var count = state.count;
    var free = this.freeFor(count);
    var remaining = this.nextTarget(count) - count;
    var full = count >= this.size; // minstens één volle deal (voor require-full)
    var r = count % this.size;
    // Milestone-aware: vanaf het eerste gratis stuk staat de bar VOL
    // (deal voelt compleet, geen push); daarvoor loopt hij naar milestone 1.
    var pct = count === 0 ? 0 : (free > 0 ? 100 : Math.min(100, (count / this.m1) * 100));
    if (this.trayBar) this.trayBar.style.width = pct + '%';

    if (this.trayCount) {
      this.trayCount.textContent = (free > 0 && this.countFreeTpl)
        ? sub(sub(this.countFreeTpl, '{count}', count), '{free}', free)
        : sub(sub(this.countTpl, '{count}', count), '{n}', this.size);
    }

    var locked = this.requireFull && !full;
    // Deal-framing alléén vóór het eerste gratis item: wie al een gratis
    // stuk heeft verdiend, krijgt gewoon een checkout-knop (geen dead-end).
    var framed = !locked && this.dealFraming && free === 0 && count > 0 && !!this.unlockTpl;

    // Tweede regel: bij 3–4 stuks een kleine bonus-hint (het succes staat al
    // in de countregel "{count} in your box — {free} FREE ✓"); op een vol blok
    // (5, 10, …) de live besparing als payoff; voorbij de eerste tier NOOIT
    // meer een nudge. Bij framing draagt de knop de eerste nudge al.
    if (this.trayValue) {
      var value = '';
      var isHint = false;
      var savings = (this.showSavings && this.savingsTpl &&
                     state.total != null && state.originalTotal != null &&
                     state.originalTotal - state.total > 0)
        ? sub(this.savingsTpl, '{savings}', money(state.originalTotal - state.total))
        : '';
      if (count > 0) {
        if (free === 0) {
          value = framed ? '' : (this.nudge1Tpl ? sub(this.nudge1Tpl, '{remaining}', remaining) : '');
        } else if (r === 0 && savings) {
          value = savings;
        } else if (count < this.size && this.nudge2Tpl) {
          value = sub(this.nudge2Tpl, '{remaining}', remaining);
          isHint = true;
        } else if (savings) {
          value = savings;
        }
      }
      this.trayValue.textContent = value;
      this.trayValue.classList.toggle('is-hint', isHint);
      this.trayValue.hidden = value === '';
    }

    if (this.trayCheckout) {
      var label;
      if (locked) {
        label = this.addMoreTpl ? sub(this.addMoreTpl, '{remaining}', remaining) : this.checkoutFallbackText;
      } else if (framed) {
        label = sub(this.unlockTpl, '{remaining}', remaining);
      } else if (this.checkoutTotalTpl && state.total != null) {
        label = sub(this.checkoutTotalTpl, '{total}', money(state.total));
      } else {
        label = this.checkoutFallbackText;
      }
      (this.trayCheckoutLabel || this.trayCheckout).textContent = label;
      if (this.trayCheckoutSub) {
        var showSub = framed && this.checkoutNowTpl && state.total != null;
        if (showSub) {
          var totalStr = money(state.total);
          if (this.strikeTotal) {
            this.trayCheckoutSub.innerHTML = sub(esc(this.checkoutNowTpl), '{total}', '<s>' + esc(totalStr) + '</s>');
          } else {
            this.trayCheckoutSub.textContent = sub(this.checkoutNowTpl, '{total}', totalStr);
          }
        }
        this.trayCheckoutSub.hidden = !showSub;
      }
      // href-vangnet: ook zonder JS-klikhandler doet de link iets zinnigs
      this.trayCheckout.setAttribute('href', locked || framed ? '#bundle' : '/checkout');
      this.trayCheckout.classList.toggle('is-incomplete', locked);
      this.trayCheckout.classList.toggle('is-framed', framed);
      this.trayCheckout.classList.remove('is-disabled'); // pre-init state opheffen
    }

    this.tray.hidden = count === 0;
    // "complete"-styling zodra het eerste gratis stuk binnen is
    this.tray.classList.toggle('is-complete', free > 0);
    this.renderStrip(state);
  };

  /* ---------------- tray fotostrip + cart drawer ---------------- */
  // Mini-thumbnails van de boxinhoud in de tray; tikken op de tray-info opent
  // de cart drawer (één overzicht met remove-knoppen en FREE-labels).
  EveBundle.prototype.renderStrip = function (state) {
    if (!this.strip || !this.stripThumbs) return;
    var items = state.items || [];
    var max = 4;
    var html = '';
    items.slice(0, max).forEach(function (it) {
      html += it.image
        ? '<img class="eve-tray__thumb" src="' + esc(it.image) + '" alt="" width="48" height="48" loading="lazy">'
        : '<span class="eve-tray__thumb"></span>';
    });
    if (items.length > max) {
      html += '<span class="eve-tray__thumb eve-tray__thumb--more">+' + (items.length - max) + '</span>';
    }
    this.stripThumbs.innerHTML = html;
    this.strip.hidden = items.length === 0;
  };

  EveBundle.prototype.openCartDrawer = function (delay) {
    setTimeout(function () {
      // eerst contents verversen, dan openen; het drawer-vangnet
      // (dialog:before-show) reconcilieert zelf met /cart.js
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      var d = document.getElementById('cart-drawer');
      if (d && typeof d.show === 'function') d.show();
    }, delay || 0);
  };

  /* ---------------- card-deselect (badge ✕ verwijdert het product) ---------------- */
  EveBundle.prototype.removeLineKey = function (key) {
    return req('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: 0 }) });
  };

  EveBundle.prototype.removeProduct = function (card, badge) {
    var self = this;
    if (!card || !this._lastState) return;
    if (badge && badge.classList.contains('is-loading')) return;
    var pid = String(card.getAttribute('data-product-id'));
    var keys = (this._lastState.items || [])
      .filter(function (it) { return String(it.productId) === pid; })
      .map(function (it) { return it.key; });
    if (!keys.length) return;
    if (badge) badge.classList.add('is-loading');
    var lastCart = null;
    // SEQUENTIEEL verwijderen: parallelle /cart/change.js-calls racen en droppen updates
    keys.reduce(function (p, key) {
      return p.then(function () { return self.removeLineKey(key); })
              .then(function (cart) { lastCart = cart; });
    }, Promise.resolve())
      .then(function () {
        if (badge) badge.classList.remove('is-loading');
        if (!lastCart) return;
        // Response-cart is de nieuwste waarheid: in-flight fetches ongeldig maken en direct renderen
        self._traySeq++;
        var st = self.cartState(lastCart);
        self.renderInbox(st);
        self.renderTray(st);
        // header-badge + cart drawer laten meesyncen
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function () {
        // deels verwijderd mogelijk → terug naar server-waarheid
        if (badge) badge.classList.remove('is-loading');
        self.refreshTray();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      });
  };

  // Klik op de vergrendelde knop: soepel terug naar het grid + korte puls
  EveBundle.prototype.nudgeToGrid = function () {
    var grid = this.root.querySelector('.eve-bundle__grid');
    if (!grid) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var startY = window.scrollY;
    grid.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    // Vangnet: komt de smooth-scroll niet op gang (geannuleerd/niet ondersteund),
    // spring er dan direct heen — de klik moet altijd zichtbaar effect hebben.
    setTimeout(function () {
      if (Math.abs(window.scrollY - startY) < 10 && grid.getBoundingClientRect().top > 120) {
        grid.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }, 350);
    grid.classList.remove('is-nudged');
    void grid.offsetWidth; // reflow zodat de animatie opnieuw start
    grid.classList.add('is-nudged');
    grid.addEventListener('animationend', function h() {
      grid.classList.remove('is-nudged');
      grid.removeEventListener('animationend', h);
    });
  };

  /* Bezig-state op de tray-CTA tijdens checkout-navigatie (86cb3gu56).
     renderTray raakt is-loading bewust niet aan (herschrijft alleen de
     label-span), dus de state overleeft elke cart:change; endCheckout wist
     hem bij terugkeer (pageshow) of via het 15s-vangnet als de navigatie
     geannuleerd wordt. Geen pointer-events:none in CSS: de klik-guard zit
     in bind() zodat de knop na het vangnet weer kan leven. */
  EveBundle.prototype.startCheckout = function (btn) {
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    var self = this;
    clearTimeout(this._checkoutTimer);
    this._checkoutTimer = setTimeout(function () { self.endCheckout(); }, 15000);
  };
  EveBundle.prototype.endCheckout = function () {
    clearTimeout(this._checkoutTimer);
    if (!this.trayCheckout) return;
    this.trayCheckout.classList.remove('is-loading');
    this.trayCheckout.removeAttribute('aria-busy');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  };

  /* ---------------- filters (multi-select: types togglen en tellen op) ---------------- */
  EveBundle.prototype.applyFilter = function (btn) {
    var val = btn.getAttribute('data-filter');
    var pills = this.root.querySelectorAll('[data-filter]');
    var allBtn = this.root.querySelector('[data-filter="all"]');
    if (val === 'all') {
      // All = alle selecties wissen
      pills.forEach(function (b) { b.classList.remove('is-active'); });
      if (allBtn) allBtn.classList.add('is-active');
    } else {
      // Type-pill togglet; meerdere tegelijk mogelijk (OR-filter)
      btn.classList.toggle('is-active');
      if (allBtn) allBtn.classList.remove('is-active');
    }
    // Actieve set bepalen; leeg → terugvallen op All (alles tonen)
    var actief = [];
    pills.forEach(function (b) {
      if (b.classList.contains('is-active') && b.getAttribute('data-filter') !== 'all') {
        actief.push(b.getAttribute('data-filter'));
      }
    });
    if (actief.length === 0 && allBtn) allBtn.classList.add('is-active');
    this.activeFilters = actief;
    // Elke filterwissel start weer compact; anders staat de bezoeker na één
    // keer uitklappen voor elk type opnieuw voor een muur van kaarten.
    this.revealLimit = this.revealInitial;
    this.renderGrid();
  };

  /* ---------------- reveal ---------------- */

  // Enige plek die card.hidden zet: filter-match én reveal-limiet lopen samen,
  // twee losse schrijvers zouden elkaars staat overschrijven.
  EveBundle.prototype.renderGrid = function () {
    var filters = this.activeFilters || [];
    var limit = this.revealLimit;
    var matched = 0;
    var shown = 0;
    // Set-kaarten (curated trio's) horen bij het volledige overzicht én bij de
    // "Sets"-pil (__sets — waarde die nooit een product-type-handle kan zijn);
    // bij een gewoon typefilter verdwijnen ze, zodat een categorie puur producten toont.
    var setCards = this.root.querySelectorAll('[data-set-card]');
    var showSets = filters.length === 0 || filters.indexOf('__sets') > -1;
    for (var s = 0; s < setCards.length; s++) {
      setCards[s].hidden = !showSets;
    }
    for (var i = 0; i < this.cards.length; i++) {
      var card = this.cards[i];
      var isMatch = filters.length === 0 || filters.indexOf(card.getAttribute('data-type')) > -1;
      var show = false;
      if (isMatch) {
        matched++;
        // Gekozen producten blijven altijd staan, ook voorbij de limiet:
        // een kaart die je net toevoegde mag niet onder je vinger verdwijnen.
        show = !limit || shown < limit || card.classList.contains('is-in-box');
        if (show) shown++;
      }
      card.hidden = !show;
    }
    if (this.moreBtn) {
      var rest = matched - shown;
      if (rest > 0) {
        this.moreBtn.textContent = this.revealMoreTpl
          ? sub(this.revealMoreTpl, '{remaining}', rest)
          : 'Show more';
        this.moreBtn.hidden = false;
      } else {
        this.moreBtn.hidden = true;
      }
    }
  };

  EveBundle.prototype.revealMore = function () {
    if (!this.revealLimit) return;
    this.revealLimit += this.revealStep;
    this.renderGrid();
  };

  // De knop is tegelijk de sentinel: komt hij in zicht, dan klapt het grid
  // vanzelf uit. Zonder IntersectionObserver blijft de knop gewoon klikbaar.
  EveBundle.prototype.initRevealObserver = function () {
    if (!this.moreBtn || !this.revealAuto || !this.revealInitial) return;
    if (!('IntersectionObserver' in window)) return;
    var self = this;
    this._revealIO = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && !self.moreBtn.hidden) self.revealMore();
      }
    }, { rootMargin: '600px 0px' });
    this._revealIO.observe(this.moreBtn);
  };

  /* ---------------- quick view ---------------- */
  EveBundle.prototype.openQuickView = function (handle, nav) {
    var self = this;
    var qv = this.root.querySelector('[data-qv]');
    var body = this.root.querySelector('[data-qv-body]');
    if (!qv || !body) return;
    // nav = {handles, index} bij een box-Quick View; card-Quick Views geven
    // niets mee en wissen daarmee de bladercontext.
    this._qvNav = (nav && nav.handles && nav.handles.length > 1) ? nav : null;
    var navEl = qv.querySelector('[data-qv-nav]');
    if (navEl) {
      navEl.hidden = !this._qvNav;
      if (this._qvNav) {
        var counter = navEl.querySelector('[data-qv-counter]');
        if (counter) counter.textContent = (this._qvNav.index + 1) + ' / ' + this._qvNav.handles.length;
      }
    }
    body.innerHTML = '<div class="eve-qv__loading">…</div>';
    qv.hidden = false;
    document.documentElement.classList.add('eve-qv-open');

    // rating komt van de card-metafields (/products/{handle}.js bevat géén metafields)
    var srcBtn = this.root.querySelector('[data-quickview][data-handle="' + handle + '"]');
    var srcCard = srcBtn ? srcBtn.closest('[data-card]') : null;
    var qvRatingVal = srcCard ? parseFloat(srcCard.getAttribute('data-rating')) : 0;
    var qvRatingCount = srcCard ? parseInt(srcCard.getAttribute('data-rating-count'), 10) : 0;
    if (!isFinite(qvRatingVal)) qvRatingVal = 0;
    if (!isFinite(qvRatingCount)) qvRatingCount = 0;

    req('/products/' + handle + '.js').then(function (p) {
      var imgs = (p.media || []).filter(function (m) { return m.media_type === 'image'; });
      if (!imgs.length && p.images && p.images.length) {
        imgs = p.images.map(function (src) { return { src: src }; });
      }

      // gallery (same slider component as the cards)
      var g = '<div class="eve-qv__gallery"><div class="eve-bundle__slider" data-slider><div class="eve-bundle__slides" data-slides>';
      imgs.forEach(function (m) {
        var src = m.src.indexOf('?') > -1 ? m.src + '&width=700' : m.src + '?width=700';
        g += '<div class="eve-bundle__slide"><img src="' + src + '" alt="" loading="lazy"></div>';
      });
      g += '</div>';
      if (imgs.length > 1) {
        g += '<button type="button" class="eve-bundle__arrow eve-bundle__arrow--prev" data-slide-prev aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg></button>';
        g += '<button type="button" class="eve-bundle__arrow eve-bundle__arrow--next" data-slide-next aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg></button>';
        g += '<div class="eve-bundle__dots" data-dots>';
        imgs.forEach(function (_, i) { g += '<span class="eve-bundle__dot' + (i === 0 ? ' is-active' : '') + '"></span>'; });
        g += '</div>';
      }
      g += '</div></div>';

      // details + options panel (reuses the card panel structure/classes)
      var d = '<div class="eve-qv__panel" data-variants-panel>';
      d += '<h3 class="eve-qv__title">' + esc(p.title) + '</h3>';
      if (self.qvRating && qvRatingCount > 0) {
        d += '<div class="eve-qv__rating">' +
          '<span class="eve-qv__rating-stars" style="--pct:' + (qvRatingVal * 20) + '%;">' +
            '<span class="eve-qv__rating-base">★★★★★</span>' +
            '<span class="eve-qv__rating-fill">★★★★★</span>' +
          '</span>' +
          '<span class="eve-qv__rating-num">' + qvRatingVal.toFixed(1) + '</span>' +
          '<span class="eve-qv__rating-count">' + qvRatingCount + ' ' + esc(self.qvReviewsLabel) + '</span>' +
        '</div>';
      }
      if (self.showPrices) {
        var qvCmp = p.compare_at_price && p.compare_at_price > p.price ? '<s>' + money(p.compare_at_price) + '</s> ' : '';
        d += '<p class="eve-qv__price">' + qvCmp + money(p.price) + '</p>';
      }

      var hasOptions = !(p.variants.length === 1 && p.variants[0].title === 'Default Title');
      if (hasOptions) {
        p.options.forEach(function (opt, idx) {
          var name = opt.name || opt;
          var values = opt.values || [];
          var single = values.length === 1 ? ' eve-bundle__optgroup--single' : '';
          d += '<fieldset class="eve-bundle__optgroup' + single + '" data-option-index="' + idx + '"><legend class="eve-bundle__optname">' + esc(name) + '</legend><div class="eve-bundle__optvalues">';
          values.forEach(function (v) {
            d += '<button type="button" class="eve-bundle__optvalue" data-option-value="' + esc(v) + '">' + esc(v) + '</button>';
          });
          d += '</div></fieldset>';
        });
      }

      var variantJson = p.variants.map(function (v) {
        return { id: v.id, available: v.available, options: v.options, price: v.price,
          featured_image: v.featured_image && v.featured_image.src ? { src: v.featured_image.src } : null };
      });
      d += '<script type="application/json" data-variant-json>' + JSON.stringify(variantJson) + '<\/script>';
      d += '<button type="button" class="eve-bundle__boxbtn" data-addtobox disabled>' +
        '<span class="eve-bundle__boxbtn-label">' + self.addToBoxText + '</span>' +
        '<span class="eve-bundle__boxbtn-added">' + self.addedText + '</span></button>';
      // stock urgency onder de knop — attrs komen van de Liquid-card (zelfde bron als data-rating)
      var uLabel = srcCard ? srcCard.getAttribute('data-urgency-label') : null;
      if (uLabel) {
        d += '<p class="ag-urgency ag-urgency--bundle-qv" data-urgency-tier="' + esc(srcCard.getAttribute('data-urgency-tier') || '') + '"' +
          ' style="--ag-urgency-color:' + esc(srcCard.getAttribute('data-urgency-color') || '') + '">' +
          '<span class="ag-urgency__dot" aria-hidden="true"></span>' + esc(uLabel) + '</p>';
      }
      // product description onder de knop (LC-volgorde); shop-eigen HTML, geen escaping
      if (p.description) d += '<div class="eve-qv__desc rte">' + p.description + '</div>';
      // beige USP-blok (PDP-stijl) onder de beschrijving; Liquid rendert de template met icons
      var uspTpl = self.root.querySelector('template[data-qv-usp]');
      if (uspTpl) d += uspTpl.innerHTML;
      d += '</div>';

      body.innerHTML = g + d;
      body.querySelectorAll('[data-slider]').forEach(function (sl) {
        initSlider(sl, { isDragged: function () { return self._dragged; } });
      });
      var panel = body.querySelector('[data-variants-panel]');
      if (hasOptions) {
        self.selectDefaults(panel);
      } else if (variantJson.length && variantJson[0].available) {
        var addBtn = panel.querySelector('[data-addtobox]');
        addBtn.disabled = false;
        addBtn.setAttribute('data-variant-id', variantJson[0].id);
      }
    }).catch(function () {
      // product fetch failed — never leave the modal hanging on the loader
      body.innerHTML = '<div class="eve-qv__loading">' + esc(self.errorText) + '</div>';
    });
  };

  // Bladeren binnen een box-Quick View, met wrap-around (na het laatste stuk weer het eerste)
  EveBundle.prototype.stepQuickView = function (dir) {
    if (!this._qvNav) return;
    var n = this._qvNav.handles.length;
    var i = (this._qvNav.index + dir + n) % n;
    this.openQuickView(this._qvNav.handles[i], { handles: this._qvNav.handles, index: i });
  };

  EveBundle.prototype.closeQuickView = function () {
    var qv = this.root.querySelector('[data-qv]');
    if (qv) {
      qv.hidden = true;
      var navEl = qv.querySelector('[data-qv-nav]');
      if (navEl) navEl.hidden = true;
    }
    this._qvNav = null;
    document.documentElement.classList.remove('eve-qv-open');
  };

  /* ---------------- init ---------------- */
  function init() {
    document.querySelectorAll('[data-eve-bundle]').forEach(function (root) {
      if (!root.__eveBundle) root.__eveBundle = new EveBundle(root);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-eve-bundle]');
    if (root && !root.__eveBundle) root.__eveBundle = new EveBundle(root);
  });
})();
